import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';

import paths from '@src/paths';
import { toast } from '@src/components/core/toaster';
import defaultClient from '@src/api/clients/default';

const STATUS_COLORS = {
  new_business: 'info',
  onboarding: 'warning',
  active_new: 'success',
  active: 'success',
  paused: 'default',
  off: 'error',
};

const STATUS_LABELS = {
  new_business: 'New',
  onboarding: 'Onboarding',
  active_new: 'Active (New)',
  active: 'Active',
  paused: 'Paused',
  off: 'Off',
};

const LD_LABELS = { not_configured: 'Not Set', awaiting_test: 'Awaiting Test', awaiting_confirm: 'Awaiting Confirm', confirmed: 'Confirmed' };
const LD_COLORS = { not_configured: 'default', awaiting_test: 'warning', awaiting_confirm: 'info', confirmed: 'success' };

function renderClientRow(c, navigate, indented) {
  return (
    <TableRow
      key={c.client_id}
      hover
      onClick={() => navigate(paths.onboarding.internal(c.client_slug))}
      sx={{ cursor: 'pointer', '& td': { py: 1, px: 1.5, fontSize: 13 } }}
    >
      <TableCell sx={indented ? { pl: 5 } : undefined}>
        <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: 13 }}>{c.client_name}</Typography>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25 }}>
          <Chip label={STATUS_LABELS[c.client_status] || c.client_status} size="small"
            color={STATUS_COLORS[c.client_status] || 'default'} sx={{ fontSize: 10, height: 18 }} />
          {c.days_in_onboarding != null && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
              {c.days_in_onboarding}d
            </Typography>
          )}
        </Stack>
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <LinearProgress variant="determinate" value={c.progress}
            sx={{ flex: 1, height: 5, borderRadius: 3 }}
            color={c.progress === 100 ? 'success' : 'primary'} />
          <Typography variant="caption" fontWeight={600} sx={{ fontSize: 11 }}>{c.progress}%</Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
          {c.completed_tasks}/{c.total_tasks}
        </Typography>
      </TableCell>
      <TableCell>
        {c.current_phase ? (
          <Chip label={c.current_phase} size="small" color="primary" variant="outlined" sx={{ fontSize: 10, height: 20, fontWeight: 600 }} />
        ) : (
          <Chip label="Done" size="small" color="success" sx={{ fontSize: 10, height: 20 }} />
        )}
      </TableCell>
      <TableCell>
        {(c.waiting_on_client || []).length > 0 ? (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {(c.waiting_on_client || []).map((w) => (
              <Chip key={w} label={w} size="small" color="warning" variant="outlined" sx={{ fontSize: 9, height: 18 }} />
            ))}
          </Stack>
        ) : (
          <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />
        )}
      </TableCell>
      <TableCell>
        <Chip
          label={LD_LABELS[c.lead_delivery_status] || '—'}
          size="small"
          color={LD_COLORS[c.lead_delivery_status] || 'default'}
          variant={c.lead_delivery_status === 'confirmed' ? 'filled' : 'outlined'}
          sx={{ fontSize: 9, height: 18 }}
        />
      </TableCell>
      <TableCell>
        <Typography variant="caption" fontWeight={600} sx={{ color: '#ff680d', fontSize: 11, whiteSpace: 'nowrap' }}>
          {c.estimated_launch ? new Date(c.estimated_launch + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
        </Typography>
      </TableCell>
      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
        <Stack direction="row" spacing={0.5}>
          <Button component={Link} to={paths.onboarding.internal(c.client_slug)}
            size="small" sx={{ fontSize: 11, minWidth: 0, px: 1 }}>Staff</Button>
          <Button component="a" href={`/onboarding/${c.client_slug}`} target="_blank"
            size="small" sx={{ fontSize: 11, minWidth: 0, px: 1 }}>Client</Button>
        </Stack>
      </TableCell>
    </TableRow>
  );
}

export default function OnboardingDashboardPage() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [collapsedParents, setCollapsedParents] = useState({});

  const loadData = async () => {
    setLoading(true);
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const res = await defaultClient.request({ url: `tasks/onboarding-tasks/dashboard/${params}` });
      setClients(res?.data?.results || []);
    } catch {
      toast.error('Failed to load onboarding dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [statusFilter]);

  // Group rows so each parent company appears as a collapsible header followed by its clients;
  // clients without a parent render as standalone rows interleaved alphabetically.
  const groupedRows = useMemo(() => {
    const parents = new Map();
    const standalone = [];
    clients.forEach((c) => {
      if (c.parent_company) {
        const key = c.parent_company.id;
        if (!parents.has(key)) {
          parents.set(key, { parent: c.parent_company, children: [] });
        }
        parents.get(key).children.push(c);
      } else {
        standalone.push(c);
      }
    });
    const groups = [
      ...Array.from(parents.values()).map((g) => ({
        type: 'parent',
        sortKey: g.parent.name || '',
        ...g,
      })),
      ...standalone.map((c) => ({ type: 'standalone', sortKey: c.client_name || '', client: c })),
    ];
    groups.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
    return groups;
  }, [clients]);

  const toggleParent = (parentId) => {
    setCollapsedParents((prev) => ({ ...prev, [parentId]: !prev[parentId] }));
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>Onboarding Dashboard</Typography>
        <Stack direction="row" spacing={2}>
          <TextField
            select size="small" label="Status" value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)} sx={{ minWidth: 160 }}
          >
            <MenuItem value="">All Active</MenuItem>
            <MenuItem value="new_business">New Business</MenuItem>
            <MenuItem value="onboarding">Onboarding</MenuItem>
          </TextField>
          <Button startIcon={<RefreshIcon />} onClick={loadData} size="small">Refresh</Button>
        </Stack>
      </Stack>

      <Card>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={32} /></Box>
          ) : clients.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography color="text.secondary">No clients in onboarding.</Typography>
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, fontSize: 11, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, py: 1, px: 1.5, whiteSpace: 'nowrap' } }}>
                  <TableCell>Client</TableCell>
                  <TableCell sx={{ minWidth: 120 }}>Progress</TableCell>
                  <TableCell>Current Step</TableCell>
                  <TableCell>Waiting On</TableCell>
                  <TableCell>Lead Delivery</TableCell>
                  <TableCell>Launch</TableCell>
                  <TableCell sx={{ width: 40 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groupedRows.map((group) => {
                  if (group.type === 'standalone') {
                    return renderClientRow(group.client, navigate, false);
                  }
                  const { parent, children } = group;
                  const collapsed = !!collapsedParents[parent.id];
                  const avgProgress = children.length
                    ? Math.round(children.reduce((s, c) => s + (c.progress || 0), 0) / children.length)
                    : 0;
                  const totalWaiting = children.reduce((s, c) => s + (c.waiting_on_client || []).length, 0);
                  return (
                    <React.Fragment key={`parent-${parent.id}`}>
                      <TableRow
                        hover
                        onClick={() => toggleParent(parent.id)}
                        sx={{
                          cursor: 'pointer',
                          bgcolor: 'grey.100',
                          '& td': { py: 0.75, px: 1.5, fontSize: 13, borderBottom: '2px solid', borderColor: 'grey.300' },
                        }}
                      >
                        <TableCell>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <IconButton size="small" sx={{ p: 0.25 }}>
                              {collapsed ? <KeyboardArrowRightIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                            </IconButton>
                            <BusinessIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" fontWeight={700} noWrap sx={{ fontSize: 13 }}>
                              {parent.name}
                            </Typography>
                            <Chip label={`${children.length} client${children.length !== 1 ? 's' : ''}`}
                              size="small" sx={{ fontSize: 10, height: 18 }} />
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <LinearProgress variant="determinate" value={avgProgress}
                              sx={{ flex: 1, height: 5, borderRadius: 3 }}
                              color={avgProgress === 100 ? 'success' : 'primary'} />
                            <Typography variant="caption" fontWeight={700} sx={{ fontSize: 11 }}>{avgProgress}%</Typography>
                          </Stack>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>avg</Typography>
                        </TableCell>
                        <TableCell />
                        <TableCell>
                          {totalWaiting > 0 ? (
                            <Chip label={`${totalWaiting} pending`} size="small" color="warning" variant="outlined"
                              sx={{ fontSize: 10, height: 18 }} />
                          ) : (
                            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />
                          )}
                        </TableCell>
                        <TableCell />
                        <TableCell />
                        <TableCell />
                      </TableRow>
                      {!collapsed && children.map((c) => renderClientRow(c, navigate, true))}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
